import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles.css';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { ProjectsPage } from './pages/Projects';

// Heavy / rarely-first-paint pages are lazy: keeps initial JS ~80% smaller
// (recharts in Trends is the biggest single contributor).
const ErrorsPage        = lazy(() => import('./pages/Errors').then((m) => ({ default: m.ErrorsPage })));
const ErrorDetailPage   = lazy(() => import('./pages/ErrorDetail').then((m) => ({ default: m.ErrorDetailPage })));
const EventDetailPage   = lazy(() => import('./pages/EventDetail').then((m) => ({ default: m.EventDetailPage })));
const TrendsPage        = lazy(() => import('./pages/Trends').then((m) => ({ default: m.TrendsPage })));
const SessionsPage      = lazy(() => import('./pages/Sessions').then((m) => ({ default: m.SessionsPage })));
const SessionDetailPage = lazy(() => import('./pages/SessionDetail').then((m) => ({ default: m.SessionDetailPage })));
const LivePage          = lazy(() => import('./pages/Live').then((m) => ({ default: m.LivePage })));
const SystemPage        = lazy(() => import('./pages/System').then((m) => ({ default: m.SystemPage })));
const UsersPage         = lazy(() => import('./pages/Users').then((m) => ({ default: m.UsersPage })));
const UserDetailPage    = lazy(() => import('./pages/UserDetail').then((m) => ({ default: m.UserDetailPage })));
const ReleasesPage      = lazy(() => import('./pages/Releases').then((m) => ({ default: m.ReleasesPage })));
const PerformancePage   = lazy(() => import('./pages/Performance').then((m) => ({ default: m.PerformancePage })));
const SlowPage          = lazy(() => import('./pages/Slow').then((m) => ({ default: m.SlowPage })));
const SettingsPage      = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsPage })));

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
});

function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<Layout />}>
            <Route index element={<ProjectsPage />} />
            <Route path="projects/:projectId/live"                 element={<Lazy><LivePage /></Lazy>} />
            <Route path="projects/:projectId/errors"               element={<Lazy><ErrorsPage /></Lazy>} />
            <Route path="projects/:projectId/errors/:fingerprint"  element={<Lazy><ErrorDetailPage /></Lazy>} />
            <Route path="projects/:projectId/events/:eventId"      element={<Lazy><EventDetailPage /></Lazy>} />
            <Route path="projects/:projectId/trends"               element={<Lazy><TrendsPage /></Lazy>} />
            <Route path="projects/:projectId/sessions"             element={<Lazy><SessionsPage /></Lazy>} />
            <Route path="projects/:projectId/sessions/:sessionId"  element={<Lazy><SessionDetailPage /></Lazy>} />
            <Route path="projects/:projectId/users"                element={<Lazy><UsersPage /></Lazy>} />
            <Route path="projects/:projectId/users/:userId"        element={<Lazy><UserDetailPage /></Lazy>} />
            <Route path="projects/:projectId/releases"             element={<Lazy><ReleasesPage /></Lazy>} />
            <Route path="projects/:projectId/performance"          element={<Lazy><PerformancePage /></Lazy>} />
            <Route path="projects/:projectId/slow"                 element={<Lazy><SlowPage /></Lazy>} />
            <Route path="projects/:projectId/settings"             element={<Lazy><SettingsPage /></Lazy>} />
            <Route path="system"                                   element={<Lazy><SystemPage /></Lazy>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// Tiny route-level Suspense — page chunk loads in tens of ms over HTTP/2.
function Lazy({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="text-slate-400 text-sm py-8">Loading…</div>}>
      {children}
    </Suspense>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><App /></StrictMode>,
);
